import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, message } from 'antd';
import { SendOutlined, RobotOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { renderMarkdownSafely } from '../../pages/DashboardUtils';

const { TextArea } = Input;

// 清理回答内容中的内容片段标记（与 NoteListAsk.js 保持一致）
const cleanAnswerContent = (content) => {
  if (!content || typeof content !== 'string') {
    return content;
  }
  let cleaned = content;

  // 移除【内容片段 X、Y】或【内容片段X、Y】等格式（中文方括号）
  cleaned = cleaned.replace(/【内容片段[^】]*】/g, '');

  // 移除 [内容片段] 格式（英文方括号）
  cleaned = cleaned.replace(/\[内容片段[^\]]*\]/g, '');

  // 移除 (内容片段) 格式（圆括号）
  cleaned = cleaned.replace(/\(内容片段[^\)]*\)/g, '');

  // 移除参考信息后面的【内容片段X、Y】标记
  // 匹配 "> 参考:标题(复制)\n【内容片段X、Y】" 这样的格式，只保留 "> 参考:标题(复制)"
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*【内容片段[^】]*】/g, '$1$2');
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*\[内容片段[^\]]*\]/g, '$1$2');
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*\(内容片段[^\)]*\)/g, '$1$2');

  // 清理多余的空行（保留最多两个连续空行）
  cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');

  // 清理行尾空白
  cleaned = cleaned.replace(/[ \t]+$/gm, '');

  return cleaned.trim();
};

/**
 * ChatPanel - AI Chat interface with new Figma design
 */
const ChatPanel = ({ selectedNote, currentFilterState = {} }) => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);
  const chatHistoryRef = useRef(null);
  const questionInputRef = useRef(null);
  const prevNoteIdRef = useRef(null);
  const historyLoadedRef = useRef(false);

  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: t('dashboard:chat_welcome', {
        defaultValue: "Ask questions about this document! Try:\n• What are the main points?\n• Summarize the key ideas\n• Are there any action items?\n• Explain this in simpler terms"
      })
    }
  ]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversation history when note changes
  useEffect(() => {
    const loadConversationHistory = async () => {
      if (!selectedNote?.id) return;
      if (prevNoteIdRef.current === selectedNote.id) return;
      prevNoteIdRef.current = selectedNote.id;

      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        const response = await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=chat`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            // Convert old format to new format
            const converted = data.messages.map(msg => ({
              id: msg.id?.toString() || Date.now().toString(),
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content,
              source: msg.source || '',
              confidence: msg.confidence,
              references: msg.references
            }));
            setMessages(converted);
          }
        }
        historyLoadedRef.current = true;
      } catch (error) {
        console.error('[ChatPanel] Failed to load conversation history:', error);
        historyLoadedRef.current = true;
      }
    };

    loadConversationHistory();
  }, [selectedNote?.id]);

  // Save chat history when it changes
  useEffect(() => {
    const saveChatHistory = async () => {
      if (!selectedNote?.id || !historyLoadedRef.current) return;
      if (messages.length <= 1) return;

      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        // Filter out error messages and their preceding user messages - they should not be persisted
        const persistableMessages = messages.filter((msg, idx) => {
          if (msg.isError) return false;
          // Remove user message right before an error response
          if (msg.role === 'user' && idx + 1 < messages.length && messages[idx + 1].isError) return false;
          return true;
        });
        if (persistableMessages.length <= 1) return;

        // Convert to old format for storage
        const converted = persistableMessages.map(msg => ({
          id: parseInt(msg.id) || Date.now(),
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content,
          source: msg.source || '',
          timestamp: new Date().toLocaleTimeString(),
          confidence: msg.confidence,
          references: msg.references
        }));

        await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ type: 'chat', messages: converted })
        });
      } catch (error) {
        console.error('[ChatPanel] Failed to save chat history:', error);
      }
    };

    const timeoutId = setTimeout(saveChatHistory, 1000);
    return () => clearTimeout(timeoutId);
  }, [messages, selectedNote?.id]);

  const handleSend = async () => {
    if (!question.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: question
    };

    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    // Build QA history
    const historyMessages = messages.filter(msg => msg.id !== '1');
    const recentHistory = historyMessages.slice(-10);
    const qaHistoryPairs = [];
    for (let i = 0; i < recentHistory.length - 1; i += 2) {
      const userMsg = recentHistory[i];
      const assistantMsg = recentHistory[i + 1];
      if (userMsg?.role === 'user' && assistantMsg?.role === 'assistant') {
        qaHistoryPairs.push({ user: userMsg.content, assistant: assistantMsg.content });
      }
    }

    const aiMessageId = (Date.now() + 1).toString();

    // Add AI placeholder
    setMessages(prev => [...prev, {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true
    }]);

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      const requestBody = {
        noteId: selectedNote.id,
        question: question,
        qaHistory: qaHistoryPairs
      };

      const response = await fetch(`${baseUrl}/notes/qa/note_read_assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error('SERVICE_TEMPORARILY_UNAVAILABLE');
      }

      const result = await response.json();
      setMessages(prev => prev.map(msg => {
        if (msg.id === aiMessageId) {
          return {
            ...msg,
            content: result.answer,
            source: result.source || '',
            confidence: 1, // confidence, not computed now
            isStreaming: false
          };
        }
        return msg;
      }));

    } catch (error) {
      console.error('[ChatPanel] QA failed:', error);
      
      const errorMessage = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('messages:qa_service_unavailable', { defaultValue: 'Sorry, the Q&A service is temporarily unavailable. Please try again later.' });
      
      setMessages(prev => prev.map(msg => {
        if (msg.id === aiMessageId) {
          return {
            ...msg,
            content: errorMessage,
            isStreaming: false,
            isError: true
          };
        }
        return msg;
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
      <style>{`
        .chat-message-content p {
          margin: 0;
          font-size: 14px;
          line-height: 20px;
          font-weight: 400;
        }
        .chat-message-content p + p {
          margin-top: 8px;
        }
        .chat-message-content ul, .chat-message-content ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        .chat-message-content li {
          font-size: 14px;
          line-height: 20px;
          font-weight: 400;
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <RobotOutlined style={{ fontSize: 16, color: '#fff' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ai_chat', { defaultValue: 'Chat with AI' })}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ask_about_document', { defaultValue: 'Ask questions about the document' })}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={chatHistoryRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            {msg.role === 'assistant' && (
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#e0e7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <RobotOutlined style={{ fontSize: 16, color: '#6366f1' }} />
              </div>
            )}
            <div style={{
              flex: 1,
              borderRadius: 16,
              padding: '12px 16px',
              backgroundColor: msg.role === 'user' ? '#eff6ff' : '#f3f4f6',
              color: '#111827'
            }}>
              {msg.isStreaming && !msg.content ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    animation: 'bounce 1s infinite',
                    animationDelay: '0ms'
                  }} />
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    animation: 'bounce 1s infinite',
                    animationDelay: '150ms'
                  }} />
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    animation: 'bounce 1s infinite',
                    animationDelay: '300ms'
                  }} />
                </div>
              ) : (
                <div
                  className="chat-message-content"
                  dangerouslySetInnerHTML={{
                    __html: msg.id === '1'
                      ? msg.content.replace(/\n/g, '<br/>').replace(/•/g, '&bull;')
                      : renderMarkdownSafely(cleanAnswerContent(msg.content))
                  }}
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    WebkitFontSmoothing: 'antialiased'
                  }}
                />
              )}
              {msg.source && msg.role === 'assistant' && (
                <div style={{
                  marginTop: 8,
                  fontSize: 12,
                  lineHeight: '16px',
                  color: '#6b7280',
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  WebkitFontSmoothing: 'antialiased'
                }}>
                  {msg.source === 'document'
                    ? t('dashboard:source_document', { defaultValue: 'Source: Document' })
                    : msg.source === 'general_knowledge'
                      ? t('dashboard:source_general_knowledge', { defaultValue: 'Source: General Knowledge' })
                      : t('dashboard:source_not_sure', { defaultValue: 'Source: Not Sure' })}
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 500,
                color: '#374151'
              }}>
                {t('common:you_short', { defaultValue: 'You' })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input Area */}
      <div style={{
        borderTop: '1px solid #e5e7eb',
        padding: 16,
        display: 'flex',
        gap: 8
      }}>
        <TextArea
          ref={questionInputRef}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={t('dashboard:ask_question', { defaultValue: 'Ask a question...' })}
          autoSize={{ minRows: 1, maxRows: 3 }}
          style={{
            flex: 1,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            lineHeight: '20px',
            resize: 'none',
            border: '1px solid #d1d5db',
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        />
        <button
          onClick={handleSend}
          disabled={!question.trim() || isLoading}
          style={{
            width: 44,
            height: 44,
            borderRadius: 8,
            border: 'none',
            backgroundColor: question.trim() && !isLoading ? '#6366f1' : '#e5e7eb',
            color: question.trim() && !isLoading ? '#fff' : '#9ca3af',
            cursor: question.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          <SendOutlined style={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
